<script setup>
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { VDataTableServer, VDataTable } from 'vuetify/labs/VDataTable'
import ContributoryFactorDrawer from '@/views/quality/master/ContributoryFactorDrawer.vue'
import ContributoryFactorService from '@/services/quality/contributoryFactor.service'
import { factorTypeTitle } from '@/enums/quality/factor.type'

const searchQuery = ref('')
const debouncedSearch = ref('')
const isConfirmDialogVisible = ref(false)

// Debounce search 500ms so typing doesn't fire a request per keystroke
let searchTimeout = null

watch(searchQuery, val => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    debouncedSearch.value = val
    page.value = 1
  }, 500)
})

definePage({
  meta: {
    name: 'incident-master-contributory-factor',
    subject: 'quality.menu',
    action: 'master_contributory_factor',
  },  
})

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order
}

// Headers
const headers = [
  { title: "", key: "data-table-expand" },
  {
    title: 'Name',
    key: 'name',
  },
  {
    title: 'Factor Type',
    key: 'factor_type',
  },
  {
    title: 'Actions',
    key: 'actions',
    sortable: false,
  },
]

const {
  data: typeListing,
  execute: fetchTypeListing,
} = await useApi(createUrl('/masters/contributory-factors', {
  query: {
    paged: 1,
    q: debouncedSearch,
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
    search: {
      parent_id: 'null',
    },
  },
}))

const types = computed(() => typeListing.value?.data)
const totalContributoryFactors = computed(() => typeListing.value?.meta?.total)

const isDrawerVisible = ref(false)
const isViewOnly = ref(false)
const typeData = ref()

const showContributoryFactor = (data = null, viewOnly = false) => {
  typeData.value = data
  isDrawerVisible.value = true
  isViewOnly.value = viewOnly
}

watch(isDrawerVisible, value => {
  if (value === false) {
    typeData.value = null
    isViewOnly.value = false
  }
})

const refConfirmDialog = ref()

const showConfirmDialog = data => {
  typeData.value = data
  isConfirmDialogVisible.value = true
}

const deleteContributoryFactor = async (confirm, data) => {
  if(data?.id && confirm){
    ContributoryFactorService.destroy(
      data.id, 
      () => {
        refConfirmDialog.value.onSuccess()
        isConfirmDialogVisible.value = false
        fetchTypeListing()
      },
      () => {
        refConfirmDialog.value.onError()
      },
    )
  }
}

watch(isConfirmDialogVisible, value => {
  if (value === false) typeData.value = null
})
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border"
    >
      <VCardText class="d-flex flex-wrap align-center py-4 gap-4">
        <!-- 👉 Search -->
        <div class="factor-search-field">
          <AppTextField
            v-model="searchQuery"
            placeholder="Search"
            density="compact"
          />
        </div>

        <VSpacer />

        <VBtn
          prepend-icon="tabler-plus"
          @click="showContributoryFactor(null)"
        >
          Add Contributory Factor
        </VBtn>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="types"
        :items-length="totalContributoryFactors"
        :headers="headers"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #expanded-row="slotProps">
          <tr class="!text-normal">
            <td
              colspan="12"
              class="level-bg-color px-6 py-6 overflow-hidden"
            >
              <VCard
                variant="outlined"
                class="table-container w-100"
              >
                <VDataTable
                  :items="slotProps.item?.children ?? []"
                  :headers="[
                    { title: 'Name', key: 'name'},
                    { title: 'Actions', key: 'actions', sortable: false },
                  ]"
                  :items-per-page="-1"
                  hide-default-footer
                  disable-sort
                  class="custom-data-table text-no-wrap overflow-auto"
                >
                  <template #item.name="{ item }">
                    <button
                      type="button"
                      class="factor-name text-body-1 font-weight-medium text-start"
                      @click="showContributoryFactor(item, true)"
                    >
                      {{ item.name }}
                    </button>
                  </template>
                  <template #item.actions="{ item }">
                    <IconBtn @click="showConfirmDialog(item)">
                      <VIcon icon="tabler-trash" />
                    </IconBtn>

                    <IconBtn @click="showContributoryFactor(item)">
                      <VIcon icon="tabler-edit" />
                    </IconBtn>
                  </template>
                </VDataTable>
              </VCard>
            </td>
          </tr>
        </template>

        <template #item.name="{ item }">
          <button
            type="button"
            class="factor-name text-body-1 font-weight-medium text-start"
            @click="showContributoryFactor(item, true)"
          >
            {{ item.name }}
          </button>
        </template>

        <template #item.factor_type="{ item }">
          <span :class="item.factor_type ? 'font-weight-medium' : 'text-medium-emphasis'">
            {{ item.factor_type ? factorTypeTitle(item.factor_type) : '—' }}
          </span>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <IconBtn @click="showConfirmDialog(item)">
            <VIcon icon="tabler-trash" />
          </IconBtn>

          <IconBtn @click="showContributoryFactor(item)">
            <VIcon icon="tabler-edit" />
          </IconBtn>
        </template>
      </VDataTableServer>
    </VCard>
    <ContributoryFactorDrawer
      v-model:isDrawerOpen="isDrawerVisible"
      :form-data="typeData"
      :view-only="isViewOnly"
      @refetch-listing="fetchTypeListing"
    />
    <ConfirmDialog
      ref="refConfirmDialog"
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure?"
      confirm-msg="Contributory Factor Deleted."
      confirm-title="Deleted!"
      cancel-title="Error!"
      cancel-msg="Something Went Wrong."
      :is-confirm-visible="false"
      :is-error-visible="false"
      :data="typeData"
      @confirm="deleteContributoryFactor"
    />
  </section>
</template>

<style>
/* Was a hardcoded #f7f7f7, which stayed light grey in dark theme. */
.level-bg-color {
  background-color: rgba(var(--v-theme-on-surface), 4%);
}

.non-selectable {
  user-select: none !important;
}

.v-data-table-footer {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}

/* Nested child table paginates client-side, so it has no footer to show. */
/* stylelint-disable-next-line no-descending-specificity */
.custom-data-table .v-data-table-footer {
  display: none !important;
}
</style>

<style lang="scss" scoped>
.factor-search-field {
  inline-size: 100%;

  @media (min-width: 600px) {
    inline-size: 20rem;
  }
}

// Rendered as a real button so the row opens from the keyboard too,
// but styled as plain text to sit naturally in the cell.
.factor-name {
  padding: 0;
  border: 0;
  background: none;
  color: rgb(var(--v-theme-on-surface));
  cursor: pointer;
  font: inherit;
  transition: color 0.15s ease;

  &:hover {
    color: rgb(var(--v-theme-primary));
  }
}
</style>
